import * as Sentry from '@sentry/nuxt'
import type { Event as SentryEvent } from '@sentry/core'
import { isAbortError } from './utility/isAbortError'

const config = useRuntimeConfig()

// Track which store size warnings have been logged to avoid spam
const loggedSizeWarnings = new Set<string>()

// Skip Sentry initialization if SENTRY_DISABLED is set to any value
if (config.public.sentry?.disabled) {
  // Sentry is disabled via SENTRY_DISABLED environment variable
} else if (
  config.public.sentry?.dsn &&
  (process.env.NODE_ENV === 'production' ||
    process.env.NODE_ENV === 'development')
) {
  Sentry.init({
    debug: config.public.sentry?.debug || false,
    dsn: config.public.sentry.dsn,
    tunnel: '/api/sentry-tunnel',
    environment:
      config.public.sentry.config?.environment ||
      process.env.NODE_ENV ||
      'development',
    release: config.public.alfredVersion?.replace('-', '.') || 'noversion',
    sendDefaultPii: true, // Enable IP address collection
    tracesSampleRate: 0.05, // 5% sample rate for performance monitoring (budget-conscious)
    integrations: function (integrations) {
      // integrations will be all default integrations

      const toAdd = [
        Sentry.zodErrorsIntegration({
          // Zod validation error integration
        }),
        Sentry.reportingObserverIntegration(),
        // Note: feedbackIntegration() creates a floating "Report a Bug" widget
        // We use showReportDialog() programmatically instead
        // Note: Some integrations are auto-enabled in @sentry/nuxt:
        // - browserTracingIntegration (when tracesSampleRate > 0)
        // - dedupeIntegration, linkedErrorsIntegration, breadcrumbsIntegration
        // - browserApiErrorsIntegration, globalHandlersIntegration, etc.
        Sentry.vueIntegration({
          tracingOptions: {
            trackComponents: true,
            hooks: ['mount', 'update', 'unmount'],
          },
        }),
        Sentry.piniaIntegration(usePinia(), {
          attachPiniaState: false, // Disabled - exhausted Sentry attachment quota
          stateTransformer: (state) => {
            /* eslint-disable @typescript-eslint/no-unsafe-assignment -- Pinia state is dynamically typed */
            const transformedState = {
              ...state,
              calendar: {
                ...state.calendar,
                state: {
                  ...state.calendar.state,
                  cache: null,
                },
              },
              facetsStore: null,
              searchStorePinia: {
                ...state.searchStorePinia,
                state: null,
                context: null,
                searchCounts: null,
                initialCounts: null,
              },
            }
            /* eslint-enable @typescript-eslint/no-unsafe-assignment */

            for (const [key, value] of Object.entries(transformedState)) {
              try {
                const serializedValue = JSON.stringify(value)
                const sizeInBytes = new Blob([serializedValue]).size
                const sizeInKB = Math.round((sizeInBytes / 1024) * 100) / 100
                if (sizeInKB > 40 && !loggedSizeWarnings.has(key)) {
                  // eslint-disable-next-line no-console -- Development diagnostic for state size
                  console.warn(`${key} is over 40KB:`, sizeInKB, 'KB')
                  loggedSizeWarnings.add(key)
                }
              } catch {
                // eslint-disable-next-line no-console -- Development diagnostic for serialization errors
                console.warn(
                  `Sentry.piniaIntegration stateTransformer error for ${key}`,
                )
              }
            }

            return transformedState
          },
        }),
        // Additional integrations for better error context (no extra cost):
        Sentry.extraErrorDataIntegration({
          // Captures all non-native error properties as extra data
          depth: 5, // How deep to traverse the error object
        }),
        Sentry.httpClientIntegration({
          // Captures failed HTTP requests with request/response info
          failedRequestStatusCodes: [[500, 599]], // Capture 4xx and 5xx errors
          failedRequestTargets: [/.*/], // Capture all failed requests
        }),
        Sentry.contextLinesIntegration({
          // Adds source code context to stack traces (client-side)
          frameContextLines: 7, // Number of lines before/after the error line
        }),
        Sentry.moduleMetadataIntegration({
          // Adds module/package info to stack frames for better debugging
        }),
        Sentry.feedbackIntegration({
          autoInject: false,
          colorScheme: 'light',
          enableScreenshot: false, // Disabled - exhausted Sentry attachment quota
        }),
      ]
      integrations.push(...toAdd)

      return integrations.filter(function (integration) {
        return integration.name !== 'Dedupe'
      })
    },
    normalizeDepth: 10,
    ignoreErrors: [
      // Your existing ignore patterns
      '403',
      /GSI_LOGGER/,
      /cookieyes/,
      /google_tag_manager is not defined/,
      /Unload event listeners are deprecated/,
      /Attribution Reporting is deprecated/,
      /Topics API is deprecated/,
      'AbortError',
      'The operation was aborted',
      'The user aborted a request',
      /operation.*aborted/i,
      /request.*aborted/i,
      /fetch.*aborted/i,
      /user.*aborted.*request/i,
      'Loading chunk',
      'Importing a module script failed',
      'Failed to fetch dynamically imported module',
      /ChunkLoadError/,
      /Loading CSS chunk/,
      /Failed to import dynamically/,
    ],
    beforeSend(event: SentryEvent) {
      // Your existing beforeSend logic
      if (typeof window !== 'undefined' && window.preventSentryLogging) {
        return null
      }

      // Filter out touchmove intervention warnings ONLY from Leaflet
      // These are caused by Leaflet's internal touch handling which uses passive:false
      // and calls preventDefault() during map panning. This is expected behavior.
      // Keep any touchmove interventions from other sources for investigation.
      // See: https://github.com/Leaflet/Leaflet/issues/7083
      const eventMessage = event.message ?? ''
      const isTouchmoveIntervention =
        eventMessage.includes('ReportingObserver') &&
        eventMessage.includes('touchmove') &&
        eventMessage.includes('cancelable=false')

      if (isTouchmoveIntervention) {
        // Check if it's from Leaflet - sourceFile can be in different locations
        // depending on how reportingObserverIntegration structures the event
        const extra = event.extra as Record<string, unknown> | undefined
        const body = extra?.body as Record<string, unknown> | undefined
        const sourceFile = String(body?.sourceFile ?? extra?.sourceFile ?? '')
        const isFromLeaflet = sourceFile.toLowerCase().includes('leaflet')
        const isSourceUnknown = !sourceFile

        if (isFromLeaflet || isSourceUnknown) {
          return null
        }
        // Keep touchmove interventions from identifiable non-Leaflet sources
      }

      // Check if any stacktrace frames contain cookieyes URLs
      /* eslint-disable @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access -- Sentry event structure */
      const hasCookieyesStack =
        event.exception?.values?.[0]?.stacktrace?.frames?.some((frame: any) =>
          frame.filename?.includes('cookieyes'),
        )

      // Check if the error message contains cookieyes
      const message: string = event.exception?.values?.[0]?.value || ''
      const hasCookieyesInMessage = message.includes('cookieyes')

      // If it's a cookieyes-related error, ignore it
      if (hasCookieyesStack || hasCookieyesInMessage) {
        return null
      }

      // Otherwise check other error patterns
      if (message.match(/GSI_LOGGER/i)) {
        return null
      }

      // Filter out chunk loading errors that might slip through ignoreErrors
      // These are handled by Nuxt's automatic reload via emitRouteChunkError config
      const isChunkLoadError =
        message.includes('Loading chunk') ||
        message.includes('Importing a module script failed') ||
        message.includes('Failed to fetch dynamically imported module') ||
        message.includes('ChunkLoadError') ||
        message.includes('Loading CSS chunk') ||
        message.includes('Failed to import dynamically')

      if (isChunkLoadError) {
        return null
      }

      // Filter out AbortErrors from navigation-based request cancellation
      if (isAbortError({ message, name: event.exception?.values?.[0]?.type })) {
        return null
      }

      // Enhance non-Error promise rejections with additional context
      if (
        event.exception?.values?.[0]?.value?.includes(
          'Non-Error promise rejection',
        )
      ) {
        // Add extra context for debugging
        event.extra = event.extra || {}
        event.extra.rejectionAnalysis = {
          message:
            'This error indicates a promise was rejected with a non-Error value',
          recommendation:
            'Check promise rejection patterns - should use reject(new Error(...)) instead of reject() or reject(primitive)',
          url: event.request?.url || window?.location?.href,
          timestamp: new Date().toISOString(),
        }

        // Add breadcrumb with rejection details
        event.breadcrumbs = event.breadcrumbs || []
        event.breadcrumbs.push({
          type: 'error',
          level: 'error',
          category: 'promise.rejection',
          message: 'Non-Error promise rejection context',
          data: {
            url: window?.location?.href,
            timestamp: new Date().toISOString(),
          },
        })
      }

      return event
      /* eslint-enable @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access */
    },
  })
}
